/**
 * ContextCanvas - Visual canvas for exploring card relationships
 *
 * Features:
 * - L-ID based coordinate layout
 * - Keyboard navigation (arrow keys)
 * - Focus/select cards
 * - Pan and zoom
 */

import { useCallback, useEffect, useMemo } from 'react'
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  useReactFlow,
  useNodesState,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import type { Card, Topic } from '@/types'
import { CardNode } from './CardNode'
import { TopicHeaderNode } from './TopicHeaderNode'
import { TimelineEndNode } from './TimelineEndNode'
import { useCanvasLayout } from './useCanvasLayout'
import './ContextCanvas.css'

// Register custom node types
const nodeTypes = {
  cardNode: CardNode,
  topicHeader: TopicHeaderNode,
  timelineEnd: TimelineEndNode,
}

interface ContextCanvasProps {
  cards: Card[]
  topic: Topic
  expandedLids: Set<string>
  onToggleExpand: (lid: string) => void
  onCollapseAll: () => void
  onCardOpen?: (lid: string) => void
  focusLid?: string | null
}

export function ContextCanvas({
  cards,
  topic,
  expandedLids,
  onToggleExpand,
  onCollapseAll,
  onCardOpen,
  focusLid,
}: ContextCanvasProps) {
  const { nodes: layoutNodes, edges } = useCanvasLayout(cards, topic, expandedLids, focusLid)
  const [nodes, setNodes, onNodesChange] = useNodesState(layoutNodes)
  const { fitView } = useReactFlow()

  // Update nodes when layout changes (but preserve user-dragged positions for unchanged nodes)
  useEffect(() => {
    setNodes((currentNodes) => {
      // For nodes whose size changed (expanded/collapsed), use new layout position
      // For other nodes, keep their current position if they were dragged
      return layoutNodes.map((layoutNode) => {
        const currentNode = currentNodes.find((n) => n.id === layoutNode.id)
        if (currentNode) {
          // Check if size changed (expanded state changed)
          const sizeChanged =
            currentNode.style?.width !== layoutNode.style?.width ||
            currentNode.style?.height !== layoutNode.style?.height

          if (sizeChanged) {
            // Size changed, use new layout position
            return layoutNode
          } else {
            // Size same, keep current position (user might have dragged it)
            return {
              ...layoutNode,
              position: currentNode.position,
            }
          }
        }
        return layoutNode
      })
    })
  }, [layoutNodes, setNodes])

  // Initial view - focus on specific card if focusLid provided, otherwise header and first cards
  useEffect(() => {
    if (layoutNodes.length > 0) {
      setTimeout(() => {
        // If focusLid is provided, center on that specific card
        if (focusLid) {
          const targetNode = layoutNodes.find(n => n.id === focusLid)
          if (targetNode) {
            fitView({
              nodes: [{ id: focusLid }],
              padding: 0.5,
              duration: 400,
              maxZoom: 1,
            })
            return
          }
        }

        // Default: focus on header and first few card nodes
        const focusNodes = layoutNodes
          .filter(n => n.id === 'topic-header' || n.type === 'cardNode')
          .slice(0, 4) // Header + first 3 cards
          .map(n => ({ id: n.id }))

        if (focusNodes.length > 0) {
          fitView({
            nodes: focusNodes,
            padding: 0.3,
            duration: 300,
            maxZoom: 1,
          })
        }
      }, 150)
    }
  }, [layoutNodes.length, fitView, layoutNodes, focusLid])

  // Handle node click - toggle expanded state
  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: { id: string }) => {
      onToggleExpand(node.id)
    },
    [onToggleExpand]
  )

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't interfere with input elements
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      // Escape to collapse all
      if (e.key === 'Escape' && expandedLids.size > 0) {
        e.preventDefault()
        onCollapseAll()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [expandedLids.size, onCollapseAll])

  // Get expanded card titles for action bar
  const expandedCards = useMemo(() => {
    return Array.from(expandedLids).map((lid) => {
      const card = cards.find((c) => c.lid === lid)
      return { lid, title: card?.title || lid }
    })
  }, [expandedLids, cards])

  return (
    <div className="context-canvas">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onNodeClick={handleNodeClick}
        nodesDraggable={true}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        defaultEdgeOptions={{
          type: 'default', // bezier curve
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Controls showInteractive={false} />
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
      </ReactFlow>

      {/* Action bar when cards are expanded */}
      {expandedLids.size > 0 && (
        <div className="context-canvas__action-bar">
          <div className="context-canvas__expanded-list">
            {expandedCards.map(({ lid, title }) => (
              <div
                key={lid}
                className="context-canvas__expanded-chip"
                onClick={() => onCardOpen?.(lid)}
                title={`Open ${title}`}
              >
                <span className="chip-lid">{lid.replace(/-/g, '/')}</span>
                <span
                  className="chip-close"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleExpand(lid)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation()
                      onToggleExpand(lid)
                    }
                  }}
                  title="Collapse"
                >
                  ×
                </span>
              </div>
            ))}
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={onCollapseAll}
          >
            Collapse All
          </button>
        </div>
      )}

      {/* Keyboard hint */}
      <div className="context-canvas__hint">
        <span>Click to expand</span>
        <span>Drag to move</span>
        <span>Scroll to zoom</span>
      </div>
    </div>
  )
}

// Wrapper with ReactFlowProvider
import { ReactFlowProvider } from '@xyflow/react'

export function ContextCanvasWithProvider(props: ContextCanvasProps) {
  return (
    <ReactFlowProvider>
      <ContextCanvas {...props} />
    </ReactFlowProvider>
  )
}

export default ContextCanvasWithProvider
